import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Globe, Instagram, ArrowUpRight } from "lucide-react";
import { FadeUp, Overline } from "./Reveal";
import { SITE, waLink, WA_DEFAULT_MSG } from "@/lib/site";

const ITEMS = [
  { icon: Phone, label: "Phone", value: SITE.phoneDisplay, href: SITE.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
  { icon: Globe, label: "Website", value: SITE.website, href: "https://www.rblexim.com", testid: "contact-website" },
  { icon: Instagram, label: "Instagram", value: SITE.instagram, href: SITE.instagramUrl, testid: "contact-instagram" },
];

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <FadeUp className="max-w-2xl">
          <Overline>Contact</Overline>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.08] tracking-tight text-navy md:text-5xl">
            Let’s Build a Long-Term Partnership
          </h2>
        </FadeUp>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <FadeUp>
            <div className="rounded-2xl border border-navy/10 bg-white p-8 md:p-10">
              <div className="flex items-center gap-4">
                <img src="/assets/brand/logo.png" alt="RBL EXIM logo" className="h-16 w-16 object-contain" />
                <div>
                  <p className="font-display text-xl font-semibold tracking-tight text-navy">RBL EXIM</p>
                  <p className="text-sm text-navy/60">{SITE.city}</p>
                </div>
              </div>

              <div className="mt-8 space-y-6">
                {ITEMS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={item.testid}
                    className="group flex items-center gap-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-gold group-hover:text-navy">
                      <item.icon size={19} />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-navy/50">
                        {item.label}
                      </span>
                      <span className="mt-0.5 block text-base font-medium text-navy group-hover:text-gold-dark">
                        {item.value}
                      </span>
                    </span>
                  </a>
                ))}
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <MapPin size={19} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-navy/50">
                      Address
                    </span>
                    <span className="mt-0.5 block text-base font-medium leading-relaxed text-navy">
                      {SITE.address}
                    </span>
                  </span>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/#enquire"
                  data-testid="contact-enquiry-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
                >
                  Send Enquiry
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <a
                  href={waLink(WA_DEFAULT_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#1eb857]"
                >
                  WhatsApp Us
                </a>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-email-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
                >
                  Email Us
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.12}>
            <div
              data-testid="contact-cta-panel"
              className="relative flex h-full min-h-[440px] flex-col justify-between overflow-hidden rounded-2xl bg-navy p-8 text-white md:p-10"
            >
              <img
                src="/assets/brand/hero-spices.jpg"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-[0.14]"
              />
              <div className="absolute inset-0 bg-navy/70" />

              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gold">
                  Start a Conversation
                </p>
                <h3 className="mt-4 font-display text-2xl font-semibold uppercase leading-tight tracking-tight md:text-4xl">
                  Let’s Build a Global Partnership
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70 md:text-base">
                  Looking for premium Indian food products, private-label
                  solutions or reliable export supply? Our team is ready to
                  discuss your product requirements, packaging, quantities and
                  export needs.
                </p>
                <ul className="mt-7 space-y-3">
                  {["Product Enquiries", "Private Label Solutions", "Export & Bulk Supply"].map((point) => (
                    <li key={point} className="flex items-center gap-3 text-sm font-medium text-white/90">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-xs font-bold text-gold">
                        ✓
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/#enquire"
                    data-testid="panel-quote-cta"
                    className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-white"
                  >
                    Request a Quote
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                  <a
                    href={waLink(WA_DEFAULT_MSG)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="panel-whatsapp-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-[#1eb857]"
                  >
                    WhatsApp Us →
                  </a>
                </div>
              </div>

              <div className="relative mt-10 border-t border-white/10 pt-5">
                <p className="font-display text-sm font-semibold tracking-[0.14em]">RBL EXIM</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                  Indian Food Exporter
                </p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/50">
                  Rajkot, Gujarat, India
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
